<template>
  <div
    v-on-clickaway="hideDropdown"
    @keyup.esc="onEscape"
    @keydown.up.prevent="typeAheadUp"
    @keydown.down.prevent="typeAheadDown"
    @keydown.enter.prevent="typeAheadSelect"
    class="dropdown v-bootstrap-select"
    :class="{'disabled': disabled}">
    <button @click="toggle" type="button" class="btn dropdown-toggle btn-light" :class="{ 'bs-placeholder': !selectedValue, 'removable': removable }">
      <div class="filter-option">
        <div class="filter-option-inner">
          <div class="filter-option-inner-inner">{{ title }}</div>
        </div>
      </div>
    </button>

    <button v-if="selectedValue && removable" type="button" class="remove-btn btn" @click="onSelect(null, -1)">
        <i class="far fa-times"></i>
    </button>

    <div v-show="show" class="dropdown-menu main-wrapper" :class="{ show: show }">
      <div v-show="searchable" class="bs-searchbox">
        <input
          :placeholder="labelSearchPlaceholder"
          class="form-control"
          type="text"
          ref="search"
          v-model="searchValue"
        >
      </div>
      <div class="inner">
        <ul class="dropdown-menu inner" :class="{ show: show }">
          <li
            v-show="searchable && filteredOptions.length === 0"
            class="v-dropdown-item"
          >{{ labelNotFound }} "{{ searchValue }}"</li>
          <li
            v-if="showDefaultOption"
            class="v-dropdown-item disabled default-option"
          >{{ labelTitle }}</li>
          <li
            v-for="(option, index) in filteredOptions"
            :key="`v-select-${index}`"
            :class="{'selected' : isSelectedOption(option, index), 'disabled': option[disabledProp]}"
            @click="onSelect(option, index)"
          ><span class="dropdown-item" :class="[ isSelectedOption(option, index) ? 'active' : '', option.class]"><span class="text">{{ getOptionLabel(option) }}</span></span></li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from "vue-clickaway";

export default {
  name: "VSelect",
  mixins: [clickaway],
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    disabledProp: {
      type: String,
      default: "disabled"
    },
    labelTitle: {
      type: String,
      default: "Nothing selected"
    },
    labelNotFound: {
      type: String,
      default: "No results matched"
    },
    labelSearchPlaceholder: {
      type: String,
      default: ""
    },
    options: {
      type: Array,
      default: () => []
    },
    searchable: {
      type: Boolean,
      default: false
    },
    showDefaultOption: {
      type: Boolean,
      default: false
    },
    textProp: {
      type: String,
      default: "text"
    },
    value: {
      type: [Object, String, Number],
      default: null
    },
    valueProp: {
      type: String,
      default: "value"
    },
    removable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      show: false,
      selectedValue: null,
      searchValue: "",
      typeAheadPointer: -1
    };
  },
  computed: {
    title() {
      return this.selectedValue
        ? this.getOptionLabel(this.selectedValue)
        : this.labelTitle;
    },
    filteredOptions() {
      if (this.searchable && this.searchValue.length > 0) {
        return this.options.filter(item => {
          if (typeof item === "object") {
            return (
              item[this.textProp]
                .toLowerCase()
                .indexOf(this.searchValue.toLowerCase()) !== -1
            );
          } else {
            return (
              item.toLowerCase().indexOf(this.searchValue.toLowerCase()) !== -1
            );
          }
        });
      }
      return this.options;
    },
    reversedOptions() {
      return [...this.filteredOptions].reverse();
    },
    lastOptionIndex() {
      return this.filteredOptions.length - 1;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {



        if (this.selectedValue && newVal == this.selectedValue.value) return

        if (newVal === null) {         
          this.onSelect(null, -1);
        } else {
          const index = this.options.findIndex(op =>
            this.isEqualOption(op, newVal)
          );
          
          this.onSelect(this.options[index], index);
        }

        
      }
    }
  },
  methods: {
    onSelect(option, index) {

      if (option && !option[this.disabledProp]) {
        this.selectedValue = option;
        this.typeAheadPointer = index;
        this.hideDropdown();
        this.$emit("input", option[this.valueProp]);
      } else if (option === null) {
        this.selectedValue = null;
        this.$emit("input", null);
      }
    },
    onEscape() {
      this.hideDropdown();
    },
    typeAheadUp() {
      if (!this.show) {
        this.show = true;
      }
      if (this.typeAheadPointer > 0) {
        const nextPointer = this.typeAheadPointer - 1;
        const option = this.filteredOptions[nextPointer];
        const isDisabled = option ? option[this.disabledProp] || false : false;
        if (!isDisabled) {
          this.typeAheadPointer--;
        } else {
          this.typeAheadPointer--;
          this.typeAheadUp();
        }
      } else {
        const nextEnabledOption = this.reversedOptions.findIndex(
          o => o[this.disabledProp] !== true
        );
        this.typeAheadPointer = this.lastOptionIndex - nextEnabledOption;
      }
    },
    typeAheadDown() {
      if (!this.show) {
        this.show = true;
      }
      if (this.typeAheadPointer < this.lastOptionIndex) {
        const nextPointer = this.typeAheadPointer + 1;
        const option = this.filteredOptions[nextPointer];
        const isDisabled = option ? option[this.disabledProp] || false : false;
        if (!isDisabled) {
          this.typeAheadPointer++;
        } else {
          this.typeAheadPointer++;
          this.typeAheadDown();
        }
      } else {
        const nextEnabledOption = this.filteredOptions.findIndex(
          o => o[this.disabledProp] !== true
        );
        this.typeAheadPointer = nextEnabledOption;
      }
    },
    typeAheadSelect() {
      if (this.filteredOptions[this.typeAheadPointer]) {
        this.onSelect(
          this.filteredOptions[this.typeAheadPointer],
          this.typeAheadPointer
        );
      }
    },
    hideDropdown() {
      this.show = false;
      this.searchValue = "";
    },
    getOptionLabel(option) {
      if (typeof option === "object") {
        return option[this.textProp];
      }
      return option;
    },
    isSelectedOption(option, index) {
      if (this.typeAheadPointer === -1 && this.selectedValue) {
        return this.isEqualOption(option, this.selectedValue);
      } else if (this.selectedValue === null) {
        return false
      }
      return this.typeAheadPointer === index;
    },
    isEqualOption(a, b) {
      if (a && b && typeof a === "object" && typeof b === "object") {
        return (
          a[this.textProp] === b[this.textProp] &&
          a[this.valueProp] === b[this.valueProp]
        );
      } else if (a && b && typeof a === "object") {
        return (
          a[this.valueProp] === b
        );
      }
      return a === b;
    },
    toggle() {
      if (!this.disabled) {
        this.show = !this.show;

        if (this.show) {
          this.onSelect(null);
          const search = this.$refs.search
          setTimeout(function(){ search.focus() }, 50);
        }
      }
    }
  }
};
</script>


<style lang="scss" scoped>
  
.main-wrapper {
  max-height: 300px;
  overflow: scroll;
}

.text {
  cursor: default;
}

</style>

